import { type Dispatch, type SetStateAction, useCallback, useState } from 'react';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { isJsmPlaygroundSignup } from '@/components/signup/utils/jsm-playground-signup-intent';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import {
	INCIDENT_TEMPLATE_ID,
	type JsmIncidentManagementIntent,
} from '../views/jsm-use-case-provisioning/jsm-incident-management-setup-state';

import type { JsmUseCaseTemplateOption } from '../views/jsm-use-case-template-choice/jsm-use-case-template-choice-constants';

export const useJsmIncidentManagementIntent = (
	setTemplateId: Dispatch<SetStateAction<string | undefined>>,
) => {
	const { accountId, isUserLoggedIn, requestInProgress } = useLoggedInUser();
	const { signupId, onDemandUrl } = useSearchParameters();
	const [intent, setIntent] = useState<JsmIncidentManagementIntent>();
	const setSelectedTemplateId = useCallback<Dispatch<SetStateAction<string | undefined>>>(
		(value) => {
			setIntent(undefined);
			setTemplateId(value);
		},
		[setTemplateId],
	);

	const selectTemplateOption = useCallback(
		(option?: JsmUseCaseTemplateOption) => {
			setSelectedTemplateId(option?.templateId);
			if (!isUserLoggedIn || !accountId || requestInProgress) return;
			if (
				option?.cardKey !== 'incident-management' ||
				option.templateId !== INCIDENT_TEMPLATE_ID ||
				isJsmPlaygroundSignup(window.location.search)
			)
				return;
			try {
				if (!checkFeatureFlagGate(featureFlags.operationsLandFlow)) return;
			} catch {
				return;
			}
			setIntent({
				source: 'selector',
				cardKey: 'incident-management',
				accountId,
				signupId,
				siteName: onDemandUrl,
			});
		},
		[accountId, isUserLoggedIn, requestInProgress, signupId, onDemandUrl, setSelectedTemplateId],
	);

	return { incidentManagementIntent: intent, selectTemplateOption, setSelectedTemplateId };
};
